<template>
  <div>
    <page-container
      v-if="blog === 'error'"
      :title="$t('blog.notFound.title')"
    >
      <v-row>
        <v-col cols="12">
          <div class="title">
            {{ $t('blog.notFound.message') }}
          </div>
        </v-col>
      </v-row>
    </page-container>
    <page-container
      v-else
      :breadcrumbs="breadcrumbs"
      :title="blog.content.attributes.title"
      :banner-image="blog.content.attributes.banner"
    >
      <v-row>
        <v-col
          cols="12"
          md="9"
        >
          <!-- eslint-disable-next-line -->
        <div
            class="body-1"
            :class="{'pa-3': $vuetify.breakpoint.mdAndUp}"
            v-html="blog.content.html"
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
        >
          <v-card
            class="mb-2 transparent"
            flat
          >
            <div v-if="!!author">
              <v-card-title class="justify-center pb-0 mb-0">
                <v-avatar
                  size="150px"
                  class="my-3"
                >
                  <img :src="author.avatar">
                </v-avatar>
              </v-card-title>
              <v-card-text class="text-center">
                <span class="subtitle-1">
                  {{ author.name }}
                </span>
              </v-card-text>
              <v-divider />
            </div>
            <v-card-text>
              <v-list
                class="pt-0 mt-0 transparent"
                dense
              >
                <v-list-item>
                  <v-list-item-action>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on">
                          mdi-calendar
                        </v-icon>
                      </template>
                      <span>Published</span>
                    </v-tooltip>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ publicationDate }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              <!-- <v-list-item v-if="blog.meta.mtimeMs !== blog.meta.ctimeMs">
                <v-list-item-action>
                  <v-tooltip top>
                    <v-icon slot="activator">
                      mdi-pencil
                    </v-icon>
                    <span>Revised</span>
                  </v-tooltip>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ blog.meta.ctime | prettyDateTime }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item> -->
              </v-list>
            </v-card-text>
          </v-card>

        <!-- <v-card class="text-xs-center py-4 elevation-2">
          <v-card-text>
            <p class="subheading">
              <strong>Verder geïnteresseerd?</strong><br>
              Of wilt u een soortgelijk onderzoek laten uitvoeren?
            </p>
            <v-btn color="green white--text" href="mailto:info@attentionarchitects.nl">
              <v-icon>email</v-icon>&nbsp;&nbsp;Laat het ons weten
            </v-btn>
          </v-card-text>
        </v-card> -->
        </v-col>
      </v-row>
    </page-container>
  </div>
</template>

<script>
import PageContainer from '@/components/PageContainer.vue'
import authors from '@/content/contact/contactList.json'
import find from 'lodash/find'
import { format } from 'date-fns'
import { fallbackDate } from '../../assets/js/util';

import {nl as nlLocale} from 'date-fns/locale'
import {enUS as enLocale} from 'date-fns/locale'

const locales = {
  nl: {locale: nlLocale},
  en: {locale: enLocale}
}

export default {
  components: { PageContainer },
  async asyncData ({ app, params, store }) {
    const lang = app.i18n.locale
    let blog
    try {
      blog = {
        content: await import(`../../content/blog/${app.i18n.locale}/${params.slug}.md`),
        meta: find(store.state.content.blogs[lang], ['slug', params.slug])
      }
      blog.publicationDate = fallbackDate(blog.content.attributes.published, blog.meta.ctimeMs)
    } catch (e) {
      blog = 'error'
    }
    return { blog, authors }
  },
  computed: {
    breadcrumbs(){
      return [
        {
          text: this.$t('nav.blog'),
          disabled: false,
          to: this.localePath('blog'),
        },
        {
          text: 'Post',
          disabled: true,
          href: ''
        }
      ]
    },
    author() {
      if (this.blog !== 'error' && this.blog.content.attributes.author) {
        return find(this.authors, ['slug', this.blog.content.attributes.author])
      }
      return undefined
    },
    publicationDate() {
      return format(this.blog.publicationDate, 'd MMMM yyyy, HH:mm', locales[this.$i18n.locale])
    }
  },
  head () {
    const path = this.$route.path.split('/').filter(segment => !!segment)
    let url = ''
    if (path[0] === 'nl') {
      url += '/' + path.splice(0, 1)
    }
    if(this.blog !== 'error') {
      return {
        title: this.blog.content.attributes.title,
        script: [{
          hid: 'breadcrumbs',
          innerHTML: JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': [{
              '@type': 'ListItem',
              'position': 1,
              'item': {
                '@id': 'https://attentionarchitects.com' + url,
                'name': 'Attention Architects'
              }
            }, {
              '@type': 'ListItem',
              'position': 2,
              'item': {
                '@id': 'https://attentionarchitects.com' + url + '/' + path[0],
                'name': 'Blog'
              }
            }, {
              '@type': 'ListItem',
              'image': 'https://attentionarchitects.com/' + this.blog.content.attributes.image,
              'position': 3,
              'item': {
                '@id': 'https://attentionarchitects.com' + url + '/' + path[0] + '/' + path[1],
                'name': this.$route.params.slug
              }
            }]
          }),
          type: 'application/ld+json',
          body: true
        },
        {
          hid: 'blogPosting',
          type: 'application/ld+json',
          body: true,
          innerHTML: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": 'https://attentionarchitects.com' + this.localePath('blog')
            },
            "headline": this.blog.content.attributes.title,
            "image": 'https://attentionarchitects.com' + this.blog.content.attributes.image,
            "genre": "cognitive psychology",
            "keywords": "cognitive psychology neuromarketing smartphone netherlands",
            // "wordcount": "1120",
            "url": 'https://attentionarchitects.com' + this.$route.path,
            "datePublished": format(this.blog.publicationDate, 'yyyy-MM-dd'),
            "dateCreated": format(this.blog.publicationDate, 'yyyy-MM-dd'),
            "dateModified": format(this.blog.meta.mtime, 'yyyy-MM-dd'),
            "description": this.blog.content.attributes.summary,
            "articleBody": this.blog.content.html,
            "publisher": {
              "@type": "Organization",
              "name": "Attention Architects",
              "logo": {
                "@type": "ImageObject",
                "url": "https://attentionarchitects.com/img/brain.png"
              }
            },
            "author": {
              "@type": "Person",
              "name": this.author.name
            }
          })
        }]
      }
    } else {
      return {
        title: this.$t('blog.notFound.title')
      }
    }
  }
}
</script>
